import { defineStore } from 'pinia'
import { usePreferencesStore, PREFERENCE_KEYS } from '~/stores/preferencesStore'
import { useAuthStore } from '~/stores/authStore'
import { useLogger } from '../composables/useLogger'
import { usePostHog } from '~/composables/usePostHog'
import { ref, computed, onUnmounted, watch } from 'vue'
import type { PreferenceKey } from '~/stores/preferencesStore'
import { ONBOARDING_METHOD, SWITCHER_TYPE } from '~/src/constants'
import { OnboardingMethod, SwitcherType } from '~/src/types'
import type { UserData } from '~/composables/useOnboardingMachine'
import { Person } from '~/src/usermodel/Person'
import { useRouter } from 'vue-router'
import { useFirebaseAnalytics } from '~/composables/useFirebaseAnalytics'
import { AUTH_EVENTS } from '~/composables/usePostHog'
import type { GoogleAuthResponse } from '~/stores/authStore'

// Define constants for onboarding elements
export const ONBOARDING_ELEMENTS = {
  ALBUMS_CREATE: 'ALBUMS_CREATE',
} as const

// Configuration for temporary dismissal timeouts (in milliseconds)
export const DISMISSAL_CONFIG = {
  DEFAULT_TIMEOUT: 90000, // 90 seconds
  TIMEOUTS: {
    [ONBOARDING_ELEMENTS.ALBUMS_CREATE]: 90000, // 90 seconds
  },
  STORAGE_PREFIX: 'onboarding_dismissal_',
} as const

// Define preference key mapping for elements
export const PREFERENCE_MAPPING = {
  [ONBOARDING_ELEMENTS.ALBUMS_CREATE]: PREFERENCE_KEYS.SPEECH_BUBBLE_FINISHED_ALBUMS_CREATE,
} as const

export type OnboardingElementKey = keyof typeof ONBOARDING_ELEMENTS

// Dismissal types
export enum DismissalType {
  TEMPORARY = 'temporary',
  PERMANENT = 'permanent'
}

// Interface for tracking temporary dismissal state
interface TemporaryDismissalState {
  dismissedAt: number;
  timerId: NodeJS.Timeout | null;
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const preferencesStore = usePreferencesStore()
  const authStore = useAuthStore()
  const logger = useLogger()
  const posthog = usePostHog()
  const analytics = useFirebaseAnalytics()
  const router = useRouter()

  // State
  const initialized = ref(false)
  const finishedElements = ref(new Set<OnboardingElementKey>())
  const temporarilyDismissedElements = ref(new Set<OnboardingElementKey>())

  // Map to store timers and dismissal timestamps
  const dismissalStates = ref(new Map<OnboardingElementKey, TemporaryDismissalState>())

  // User data
  const userData = ref<UserData>({
    name: "",
    email: "",
    usage: "",
    notifications: false,
    neworexisting: "",
    existing_user_action: "",
    authType: "",
    userfirstname: "",
    helperFirstName: "",
    helperLastName: "",
    password: "",
  })

  // Google auth data
  const googleCreds = ref<GoogleAuthResponse | null>(null)
  
  // UI state
  const isLoading = ref(false)
  const isVideoPlaying = ref(false)
  
  // Error states
  const isAuthError = ref(false)
  const authErrorMessage = ref<string | null>(null)
  const isGoogleAuthError = ref(false)
  const googleAuthErrorMessage = ref<string | null>(null)
  
  // Modal state
  const isExistingUserModalOpen = ref(false)
  const existingUserEmail = ref('')

  // Getters
  const isElementFinished = (element: OnboardingElementKey) => finishedElements.value.has(element)
  const isElementTemporarilyDismissed = (element: OnboardingElementKey) => temporarilyDismissedElements.value.has(element)


  const canShowAlbumBubble = computed(() =>
    !authStore.isLockedToUserDevice &&
    initialized.value &&
    !isElementFinished(ONBOARDING_ELEMENTS.ALBUMS_CREATE) &&
    !isElementTemporarilyDismissed(ONBOARDING_ELEMENTS.ALBUMS_CREATE)
  )

  // Get the time remaining for a temporarily dismissed element (in milliseconds)
  const getTimeRemaining = (element: OnboardingElementKey): number => {
    const state = dismissalStates.value.get(element)
    if (!state || !state.dismissedAt) return 0

    const timeout = DISMISSAL_CONFIG.TIMEOUTS[element] || DISMISSAL_CONFIG.DEFAULT_TIMEOUT
    const elapsed = Date.now() - state.dismissedAt
    const remaining = Math.max(0, timeout - elapsed)
    return remaining
  }

  // Clear a temporary dismissal
  const clearTemporaryDismissal = (element: OnboardingElementKey) => {
    // Remove from temporarily dismissed set
    temporarilyDismissedElements.value.delete(element)

    // Clear timer if exists
    const state = dismissalStates.value.get(element)
    if (state && state.timerId) {
      clearTimeout(state.timerId)
    }

    // Remove state
    dismissalStates.value.delete(element)
  }

  // Actions
  async function initializeStore() {
    console.log('Navigation Model: onboardingStore.ts - initializeStore started')
    initialized.value = false
    finishedElements.value.clear()
    temporarilyDismissedElements.value.clear()

    // Clear all timers
    for (const state of dismissalStates.value.values()) {
      if (state.timerId) {
        clearTimeout(state.timerId)
      }
    }
    dismissalStates.value.clear()

    // Load all finished elements from preferences
    await Promise.all(
      Object.keys(ONBOARDING_ELEMENTS).map(async (element) => {
        const key = element as OnboardingElementKey
        const preferenceKey = PREFERENCE_MAPPING[key]
        const value = await preferencesStore.getValue(preferenceKey)

        if (value === 'true') {
          finishedElements.value.add(key)
        }
      })
    )

    initialized.value = true
    console.log('Navigation Model: onboardingStore.ts - initializeStore completed')
    console.log('Navigation Model: onboardingStore.ts - finishedElements after init:', [...finishedElements.value])
    console.log('Navigation Model: onboardingStore.ts - temporarilyDismissedElements after init:', [...temporarilyDismissedElements.value])
  }

  async function dismissElement(element: OnboardingElementKey, type: DismissalType = DismissalType.PERMANENT) {
    if (type === DismissalType.PERMANENT) {
      logger.debug('onboarding: dismissElement - permanent dismissal', { element: element })
      // Clear any temporary dismissal first
      clearTemporaryDismissal(element)

      // Set permanent dismissal
      const preferenceKey = PREFERENCE_MAPPING[element]
      await preferencesStore.setValue(preferenceKey, 'true')
      finishedElements.value.add(element)

      // Log analytics
      posthog.capture(`bubble_dismissed_permanent_${element.toLowerCase()}`)
    } else {
      // Temporary dismissal - only stored in memory
      temporarilyDismissedElements.value.add(element)

      // Set timeout to clear the temporary dismissal
      const timeout = DISMISSAL_CONFIG.TIMEOUTS[element] || DISMISSAL_CONFIG.DEFAULT_TIMEOUT
      const timerId = setTimeout(() => {
        clearTemporaryDismissal(element)
      }, timeout)

      // Store the timer and timestamp
      dismissalStates.value.set(element, {
        dismissedAt: Date.now(),
        timerId
      })

      // Log analytics
      posthog.capture(`bubble_dismissed_temporary_${element.toLowerCase()}`)
    }
  }

  // Legacy method for backward compatibility
  async function markElementAsFinished(element: OnboardingElementKey) {
    logger.debug('onboarding: markElementAsFinished - marking element as finished', { element: element })
    return dismissElement(element, DismissalType.PERMANENT)
  }

  async function resetElement(element: OnboardingElementKey) {
    // Clear temporary dismissal
    clearTemporaryDismissal(element)

    // Clear permanent dismissal
    const preferenceKey = PREFERENCE_MAPPING[element]
    await preferencesStore.removeValue(preferenceKey)
    finishedElements.value.delete(element)
  }

  async function resetAllElements() {
    // Reset all onboarding elements
    await Promise.all(
      Object.keys(ONBOARDING_ELEMENTS).map(element =>
        resetElement(element as OnboardingElementKey)
      )
    )
  }

  // Cleanup function to clear all timers when the store is no longer used
  function cleanup() {
    for (const state of dismissalStates.value.values()) {
      if (state.timerId) {
        clearTimeout(state.timerId)
      }
    }
    dismissalStates.value.clear()
  }

  // Initialize store on creation (client-side only)
  if (process.client) {
    initializeStore()
  }

  // Clean up timers when component is unmounted
  onUnmounted(() => {
    cleanup()
  })

  // Video control functions
  const startPlayingVideo = () => {
    isVideoPlaying.value = true
  }
  
  const stopPlayingVideo = () => {
    isVideoPlaying.value = false
  }

  // Check if email is already in use
  const checkExistingUser = async (email: string): Promise<boolean> => {
    logger.debug("onboardingStore: checkExistingUser", {email})
    try {
      const result = await Person.getPersonAndProviderByEmail(email)
      logger.debug("onboardingStore: checkExistingUser result", {result})
      
      if (result?.person) {
        // Email is already in use, store email and show modal
        existingUserEmail.value = email
        isExistingUserModalOpen.value = true
        return true
      }
      return false
    } catch (error) {
      logger.logErrorCaught('Error checking for existing user', 'onboardingStore.ts', error)
      return false
    }
  }

  // Validate email authentication fields
  const validateEmailAuth = async (): Promise<boolean> => {
    // Reset any previous errors
    isAuthError.value = false
    authErrorMessage.value = null
    
    // Set loading state
    isLoading.value = true
    
    try {
      // Check if all required fields are filled
      if (!userData.value.email) {
        isAuthError.value = true
        authErrorMessage.value = "Email is required"
        return false
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(userData.value.email)) {
        isAuthError.value = true
        authErrorMessage.value = "Please enter a valid email address"
        return false
      }
      
      // Check if email is already in use
      const isEmailInUse = await checkExistingUser(userData.value.email)
      if (isEmailInUse) {
        return false
      }
      
      if (!userData.value.password) {
        isAuthError.value = true
        authErrorMessage.value = "Password is required"
        return false
      }
      
      // Check password length
      if (userData.value.password.length < 6) {
        isAuthError.value = true
        authErrorMessage.value = "Password must be at least 6 characters"
        return false
      }
      
      return true
    } finally {
      // Reset loading state
      isLoading.value = false
    }
  }

  // Handle Google authentication success
  const handleGoogleUserData = async (googleAuthResponse: GoogleAuthResponse): Promise<boolean> => {
    logger.debug("onboardingStore: handleGoogleUserData", {googleAuthResponse})
    
    // Check if email is already in use
    const isEmailInUse = await checkExistingUser(googleAuthResponse.email)
    if (isEmailInUse) {
      return false
    }
    
    // Store Google credentials for later use
    googleCreds.value = googleAuthResponse
    
    // Update userData with Google info for helper
    userData.value.email = googleAuthResponse.email
    userData.value.name = `${googleAuthResponse.firstName} ${googleAuthResponse.lastName}`
    
    // Reset any previous errors
    isGoogleAuthError.value = false
    googleAuthErrorMessage.value = null
    
    return true
  }

  // Handle Google authentication error
  const handleGoogleError = (message: string) => {
    logger.debug("onboardingStore: handleGoogleError", {message})
    isGoogleAuthError.value = true
    googleAuthErrorMessage.value = message
  }

  // Handle existing user modal close
  const handleExistingUserModalClose = (shouldLogin = true) => {
    isExistingUserModalOpen.value = false
    
    if (shouldLogin) {
      // User will be redirected to login page
      return { action: 'redirect', path: '/login' }
    } else {
      // Reset auth data
      googleCreds.value = null
      userData.value.email = ""
      userData.value.password = ""
      isGoogleAuthError.value = false
      googleAuthErrorMessage.value = null
      isAuthError.value = false
      authErrorMessage.value = null
      
      // Return action to go back to auth type step
      return { action: 'goto', step: 'authType' }
    }
  }

  // Submit the final registration
  const handleFinalSubmit = async (): Promise<void> => {
    logger.debug("onboardingStore: handleFinalSubmit", {userData: userData.value, googleCreds: googleCreds.value})
    
    // Set loading state
    isLoading.value = true
    
    try {
      // Reset any previous errors
      isGoogleAuthError.value = false
      googleAuthErrorMessage.value = null
      isAuthError.value = false
      authErrorMessage.value = null
      
      if (userData.value.authType === "google" && googleCreds.value) {
        // Use the Google credentials to register the user and helper
        // Make sure we have valid names before registration
        let helperFirstName = userData.value.helperFirstName;
        let userFirstName = userData.value.userfirstname;
        
        // If helper name is empty, just use empty strings
        if (!helperFirstName) {
          helperFirstName = "";
        }
        
        // If user name is empty, just use empty string
        if (!userFirstName) {
          userFirstName = "";
        }
        
        await authStore.registerNewUserAndMainHelperWithGoogle(
          googleCreds.value,
          googleCreds.value.email,
          helperFirstName,
          "",  // Empty string for helper last name
          userFirstName,
          "" // Empty string for user's last name
        )
      } else if (userData.value.authType === "email") {
        // Use email/password to register the user and helper
        await authStore.registerNewUserAndMainHelper(
          userData.value.email,
          userData.value.password,
          userData.value.helperFirstName, // Use collected helper name
          "", // Empty string for helper last name
          userData.value.userfirstname, // User's first name from wizard
          "" // Empty string for user's last name
        )
      } else {
        throw new Error("Invalid authentication method")
      }
      
      // Log completion events
      await posthog.capture(AUTH_EVENTS.SIGNUP_COMPLETE)
      await analytics.logEvent(AUTH_EVENTS.SIGNUP_COMPLETE)

      // Navigate to appropriate page after successful registration
      if (ONBOARDING_METHOD === OnboardingMethod.ClockFocused) {
        await router.push('/user/clock')
      } else if (ONBOARDING_METHOD === OnboardingMethod.IndexPageHelper) {
        await router.push('/')
      } else if (ONBOARDING_METHOD === OnboardingMethod.ClockFocusedToUserHome) {
        await router.push('/user/home')
      } else if (ONBOARDING_METHOD === OnboardingMethod.Wizard) {
        await router.push('/user/clock')
      } else {
        logger.error('Unknown onboarding method', { onboardingMethod: ONBOARDING_METHOD })
        await router.push('/')
      }
    } catch (error) {
      logger.logErrorCaught('Error registering user', 'onboardingStore.ts', error)
      
      // Set appropriate error based on auth type
      if (userData.value.authType === "google") {
        isGoogleAuthError.value = true
        googleAuthErrorMessage.value = "Failed to create account with Google. Please try again."
      } else {
        isAuthError.value = true
        authErrorMessage.value = "Failed to create account. Please check your information and try again."
      }
      
      throw error // Re-throw to let the component handle it
    } finally {
      // Reset loading state
      isLoading.value = false
    }
  }

  // Handle QR code detection
  const handleQrDetected = async (encryptedUserId: string) => {
    logger.debug("onboardingStore: handleQrDetected", {encryptedUserId})
    
    // Log the QR code detection
    posthog.capture("OnboardWiz_QrCodeDetected")
    
    await authStore.loginToUserDeviceFromQR(encryptedUserId)
    router.push('/user/home')
  }

  // Reset UI states
  const resetUIState = () => {
    isLoading.value = false
    isVideoPlaying.value = false
    isAuthError.value = false
    authErrorMessage.value = null
    isGoogleAuthError.value = false
    googleAuthErrorMessage.value = null
    isExistingUserModalOpen.value = false
  }

  return {
    // State
    initialized,
    finishedElements,
    temporarilyDismissedElements,
    dismissalStates,
    userData,
    googleCreds,
    isLoading,
    isVideoPlaying,
    isAuthError,
    authErrorMessage,
    isGoogleAuthError,
    googleAuthErrorMessage,
    isExistingUserModalOpen,
    existingUserEmail,

    // Getters
    isElementFinished,
    isElementTemporarilyDismissed,
    canShowAlbumBubble,
    getTimeRemaining,

    // Actions
    initializeStore,
    dismissElement,
    markElementAsFinished,
    resetElement,
    resetAllElements,
    cleanup,
    startPlayingVideo,
    stopPlayingVideo,
    checkExistingUser,
    validateEmailAuth,
    handleGoogleUserData,
    handleGoogleError,
    handleExistingUserModalClose,
    handleFinalSubmit,
    handleQrDetected,
    resetUIState
  }
}) 